import React from 'react';
import { MessageSquare, Search, FileText, Send, Plane, Home, CheckCircle } from 'lucide-react';
import { applicationSteps } from '../data/mock';

const ApplicationProcess = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Başvuru Süreci</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Çekya'da öğrenci olmanız için izlediğimiz adım adım süreç. Her aşamada yanınızdayız.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {applicationSteps.map((step, index) => {
              const IconComponent = {
                MessageSquare,
                Search,
                FileText,
                Send,
                Plane,
                Home
              }[step.icon];

              return (
                <div key={step.id} className="relative pb-12">
                  {index !== applicationSteps.length - 1 && (
                    <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-amber-200"></div>
                  )}
                  
                  <div className="flex gap-6">
                    <div className="relative z-10 w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    
                    <div className="flex-1 bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="text-sm text-amber-600 font-semibold mb-1">Adım {step.id}</div>
                          <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                        </div>
                        <div className="bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">
                          {step.duration}
                        </div>
                      </div>
                      <p className="text-gray-600 text-lg">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Gerekli Belgeler</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Lisans Başvurusu İçin</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Lise diploması ve transkript</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Dil yeterlilik belgesi</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Motivasyon mektubu</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Pasaport fotokopisi</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Biyometrik fotoğraf</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Yüksek Lisans Başvurusu İçin</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Lisans diploması ve transkript</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Dil yeterlilik belgesi</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Detaylı motivasyon mektubu</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">2 referans mektubu</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Akademik CV</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700">Araştırma önerisi (bazı programlar)</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-lg mt-8">
              <p className="text-gray-700">
                <strong className="text-amber-900">Not:</strong> Tüm belgeler İngilizce veya Çekçe olmalıdır. 
                Yeminli tercüme işlemleri için size yardımcı olabiliriz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Başvuru Takvimi</h2>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <div className="text-4xl font-bold text-amber-600 mb-2">Eylül - Kasım</div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">Güz Dönemi</div>
                  <p className="text-gray-600">Başvurular genellikle Ocak-Nisan arasında alınır</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-600 mb-2">Şubat - Nisan</div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">Bahar Dönemi</div>
                  <p className="text-gray-600">Başvurular genellikle Ekim-Aralık arasında alınır</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-600 mb-2">Haziran - Ağustos</div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">Yaz Dönemi</div>
                  <p className="text-gray-600">Dil kursları için yıl boyunca başvuru</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplicationProcess;